import { Activity, ChevronRight, History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import type { StructuralAnalysisHistoryEntryView, StructuralAnalysisRunView } from "@/features/ai";
import type { AiAccessPromptView, AiGuardNoticeView } from "@/features/ai";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import type { StructuralAnalysisAvailability } from "@/features/story";

type StructuralAnalysisPanelProps = {
  activeRun?: StructuralAnalysisRunView | null;
  availability: StructuralAnalysisAvailability;
  busy?: boolean;
  errorMessage?: string | null;
  guardNotices?: AiGuardNoticeView[];
  history?: StructuralAnalysisHistoryEntryView[];
  onOpenRun?: (runId: string) => void;
  onOpenWorkspace?: () => void;
  onPromptAction?: () => void;
  onRunAnalysis?: () => void;
  prompt?: AiAccessPromptView | null;
  statusLabel?: string | null;
};

export function StructuralAnalysisPanel({
  activeRun = null,
  availability,
  busy = false,
  errorMessage = null,
  guardNotices = [],
  history = [],
  onOpenRun,
  onOpenWorkspace,
  onPromptAction,
  onRunAnalysis,
  prompt = null,
  statusLabel = null,
}: StructuralAnalysisPanelProps) {
  const { t } = useTranslation();
  const structuralAnalysisCreditCost = useRuntimeCreditCost("structural_analysis");
  const requestBlocked =
    prompt?.kind === "budget-restricted" || prompt?.kind === "wait";
  const previewHistory = history.slice(0, 3);
  const totalFindings = activeRun
    ? activeRun.sections.reduce(
        (count, section) => count + section.findings.length,
        0,
      )
    : 0;

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          {t("story.ai.structural.eyebrow")}
        </p>
        <h3 className="text-xl text-foreground">{t("story.ai.structural.title")}</h3>
        <p className="text-sm leading-6 text-muted-foreground">
          {t("story.ai.structural.body")}
        </p>
      </div>

      {errorMessage ? (
        <div
          className="rounded-[1.1rem] border border-destructive/25 bg-destructive/5 px-4 py-4 text-sm text-destructive"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      {prompt ? (
        <div className="space-y-3 rounded-[1.1rem] border border-amber-300/40 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
          <p>{t(prompt.descriptionKey, prompt.descriptionValues)}</p>
          {prompt.ctaLabelKey && onPromptAction ? (
            <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
              {t(prompt.ctaLabelKey)}
            </Button>
          ) : null}
        </div>
      ) : null}

      <AiGuardNoticeList notices={guardNotices} />

      {!availability.eligible ? (
        <div className="border-y border-ink/10 py-4 text-sm leading-6 text-muted-foreground">
          {availability.reason}
        </div>
      ) : null}

      {activeRun ? (
        <div className="space-y-4 border-b border-ink/10 pb-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-[#3b3129] text-[#fff9f0]" variant="secondary">
              {activeRun.scopeLabelSnapshot}
            </Badge>
            <Badge variant="outline">
              {t("story.ai.structural.report.findingCount", {
                count: totalFindings,
              })}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("story.ai.structural.report.createdLabel")}{" "}
            {new Date(activeRun.createdAt).toLocaleString()}
          </p>
          <Button onClick={onOpenWorkspace} size="sm" type="button" variant="ghost">
            {t("story.ai.structural.openWorkspace", {
              defaultValue: "Open full analysis",
            })}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {t("story.ai.structural.history.title", {
                defaultValue: "Saved analyses",
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("story.ai.structural.history.subtitle", {
                defaultValue: "Open a saved run in an expanded review window.",
              })}
            </p>
          </div>
          <History className="mt-1 h-4 w-4 text-muted-foreground" />
        </div>

        {previewHistory.length ? (
          <div className="divide-y divide-ink/10 border-y border-ink/10">
            {previewHistory.map((entry) => (
              <button
                className="flex w-full items-start justify-between gap-3 py-4 text-left transition-colors hover:text-clay"
                key={entry.runId}
                onClick={() => {
                  onOpenRun?.(entry.runId);
                  onOpenWorkspace?.();
                }}
                type="button"
              >
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {entry.scopeLabel}
                  </p>
                  <p className="line-clamp-2 text-sm leading-6 text-muted-foreground">
                    {entry.previewText}
                  </p>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-ink/10 pt-4">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {statusLabel ?? t("story.ai.structural.creditStatus", {
            cost: structuralAnalysisCreditCost,
            remaining: 0,
          })}
        </span>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            disabled={busy || !availability.eligible || requestBlocked}
            onClick={onRunAnalysis}
            type="button"
            variant="outline"
          >
            <Activity className="h-4 w-4" />
            {busy ? t("story.ai.structural.running") : t("story.ai.structural.runAction")}
          </Button>
        </div>
      </div>
    </section>
  );
}
